import React, { useEffect, useState } from "react";
import Review from "./ReviewCard"; // Note: ReviewCard is the new component name
import ReviewForm from "./ReviewForm";
import { getReviews } from "./ReviewService";
import Modal from "../../utilities/components/Modal";
import { useUserRefresh } from "../user/UserProvider";
import KeycloakService from "../config/KeycloakService";
import StarRating from "../../utilities/components/StarRating";
import LocalizedModal from "../../utilities/components/LocalizedModal";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import { ToastContainer } from "react-toastify";
import { MessageSquarePlus, Star } from "lucide-react";

const Reviews = () => {
  const [reviewsData, setReviewsData] = useState(EntitiesListResponseModel);
  const [showLoggingDialog, setShowLoggingDialog] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const setFetchUserFlag = useUserRefresh();

  const singIn = async () => {
    await KeycloakService.doLogin(setFetchUserFlag);
  };

  const handleShowReviewForm = () => {
    if (KeycloakService.isLoggedIn()) {
      setShowReviewForm(true);
    } else {
      setShowLoggingDialog(true);
    }
  };

  useEffect(() => {
    getReviews(setReviewsData, {});
  }, [refresh]);

  // Calculate stats
  const totalReviews = reviewsData.content?.length || 0;
  const reviewSum =
    reviewsData.content?.reduce((total, review) => total + review.rating, 0) ||
    0;
  const averageRating = totalReviews
    ? (reviewSum / totalReviews).toFixed(1)
    : "0.0";

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
      {/* --- Hero / Header Section --- */}
      <section className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Text Content */}
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 dark:text-white mb-4">
                Loved by Locals
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-xl">
                See what our community has to say about their experiences at Le
                Prestige Hall.
              </p>
            </div>

            {/* Aggregate Stats Card */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex items-center gap-6">
              <div className="text-center border-r border-slate-100 dark:border-slate-700 pr-6">
                <span className="text-4xl font-bold text-slate-900 dark:text-white block">
                  {averageRating}
                </span>
                <div className="flex justify-center mt-1">
                  <StarRating rating={parseFloat(averageRating)} size={4} />
                </div>
              </div>
              <div className="text-left">
                <span className="block text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Total Reviews
                </span>
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  {totalReviews}
                </span>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleShowReviewForm}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-600/20"
            >
              <MessageSquarePlus size={20} /> Write a Review
            </button>
          </div>
        </div>
      </section>

      {/* --- Reviews Grid --- */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-6 max-w-7xl">
          {reviewsData.content && reviewsData.content.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewsData.content
                .sort((a, b) => new Date(b.postedDate) - new Date(a.postedDate)) // Newest first
                .map((review) => (
                  <Review key={`review-${review.id}`} review={review} />
                ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6 text-slate-300">
                <Star size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                No reviews yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Be the first to share your experience!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* --- Review Form Modal --- */}
      {showReviewForm && (
        <ReviewForm setState={setRefresh} toggleModal={setShowReviewForm} />
      )}

      {/* --- Login Modal --- */}
      <Modal isOpen={showLoggingDialog} onClose={setShowLoggingDialog}>
        <LocalizedModal
          actionHandler={singIn}
          actionText="Sign In"
          cancelText="Go back"
          message="Have something to say? Sign in or register first."
          setOpen={setShowLoggingDialog}
          title="Sign In"
        />
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default Reviews;
